import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { CropImage } from "../../components/utils/ImageTreatment";
import { useTranslation } from "react-i18next";
import useWindowDimensions from "../../components/formatted/UseWindowsDimensions";
import ActivityTimeLineTooltip from "./ActivityTooltip";
import { toFrenchNumber } from "../../components/formatted/FormattedText";
import { useCollectionRowData } from "./hooks/useCollectionRowData";
import { useCollectionRowClient } from "./hooks/useCollectionRowClient";
import CollectionRowHeader from "./components/CollectionRowHeader";
import CollectionRowTags from "./components/CollectionRowTags";
import CollectionRowInfo from "./components/CollectionRowInfo";
import CollectionRowModals from "./components/CollectionRowModals";
import {
  getAzureBlob,
  saveContainerWeight,
  updateContentCategories,
  getTemporalData,
  getAzureBlobFile,
  createCharacReview,
} from "../../services/container";
import {
  canCharacterizeFromHistorique,
  getCameraId,
  getCharacterizeFromHistoriqueBlockReasonKey,
  parseCameraImageUrl,
  reduceImageQuality,
} from "../../shared/utils/utils";
import {
  buildCommentAuthorFromStorage,
  parseCommentsList,
} from "../../shared/utils/commentAuthor";
import {
  applyWasteCategoryToCycleSurfaces,
  resolveHistoryEntriesForFluxChange,
} from "./utils/applyWasteCategoryToCycleSurfaces";
import { UI_CONSTANTS } from "../../constants/ui";
import { getActivityImageFrameStyle } from "./utils/activityImageFrame";
import { buildCollectionRowChartData } from "./utils/buildCollectionRowChartData";
import SortingQualityFooter from "../../ui/components/SortingQualityFooter";
import { pdf } from "@react-pdf/renderer";
import { azureSASTokenToUrl } from "../../components/formatted/FormattedAzureSasToken";
import { starsCalculation } from "./ActivityUtilities";
import {
  GenerateAndDownloadFillingCyclePdf,
  getUniqueHistoryCycle,
  extractCollections,
} from "./GenerateAndDownloadFillingCyclePdf";
import { mergePdfs } from "../../components/download/pdfUtils";
import PdfImageSelectionModal from "./components/PdfImageSelectionModal";
import {
  buildMetricsDateRangeFromHistoryCycle,
  enqueueWasteStatisticsMetrics,
} from "../../services/wasteStatisticsMetrics";
import moment from "moment";
import { toast } from "react-toastify";
import { formattedColorMaterial } from "../../components/formatted/FormattedText";
import { useHistory, useLocation } from "react-router-dom";
import {
  ACTIVITY_DETAIL_STORAGE_KEY,
  resolveActiveWasteCategoryFromName,
} from "./utils/collectionRowUtils";

const ActivityCollectionRow = ({
  timeLine,
  index,
  loadScreen,
  handleCsvExport,
  wasteCategory,
  superContainers,
  setActiveWasteCategory,
  activeWasteCategory,
  weightToTonnes,
  setLoadScreen,
  setIsProgress,
  setChartData,
  chartRef,
  cycleChartRef,
  setDisplayChartData,
  materialChartRef,
  superContainerTimeLine,
  corporationClients,
}) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const azureUrl = process.env.REACT_APP_AZURE_URL;
  const { token, ...user } = JSON.parse(localStorage.getItem("user"));
  const [itemIndex, setItemIndex] = useState(null);
  const [comment, setComment] = useState({});
  const [displayClient, setDisplayClient] = useState(false);
  const [displayTags, setDisplayTags] = useState(false);
  const [displayStatus, setDisplayStatus] = useState(false);
  const [displayComment, setDisplayComment] = useState(false);
  const [newClient, setNewClient] = useState(false);
  const [totalWeight, setTotalWeight] = useState("");
  const [openFileModal, setOpenFileModal] = useState(false);
  const [outputClient, setOutputClient] = useState({});
  const [imageFrameSize, setImageFrameSize] = useState(
    UI_CONSTANTS.IMAGES.COLLECTION_THUMBNAIL_SIZE,
  );
  const [imageInnerSize, setImageInnerSize] = useState(
    UI_CONSTANTS.IMAGES.COLLECTION_THUMBNAIL_INNER_SIZE,
  );
  const [savingStatus, setSavingStatus] = useState(false);
  const [openModalExport, setOpenModalExport] = useState(false);
  const [pdfFile, setPdfFile] = useState([]);
  const [initialPdfModalAction, setInitialPdfModalAction] = useState("export");
  const [openModalToCharacterization, setOpenModalToCharacterization] =
    useState(false);
  const [reportingOptions, setReportingOptions] = useState(false);
  const [exportFiles, setExportFiles] = useState([]);
  const [openPdfImageSelectionModal, setOpenPdfImageSelectionModal] =
    useState(false);
  const { widthWindow } = useWindowDimensions();
  const collectionData = useSelector((state) => state.collection.data);
  const weightRef = useRef(null);

  // Utiliser les hooks pour la gestion des données
  const {
    loading,
    historyCycle,
    numberOfPackmat,
    numberOfStars,
    numberOfAnomaly,
    totalVolumeSortingError,
    totalWasteVolume,
    totalMassSortingError,
    collectionFillingLevel,
    dropOff,
    imageData,
    reviewComments,
    refetch: refetchRowData,
  } = useCollectionRowData(timeLine, superContainerTimeLine);

  const {
    clientRegisteredWeight,
    setClientRegisteredWeight,
    wasteName,
    setWasteName,
    comments,
    setComments,
    checkStatus,
    setCheckStatus,
    rawCheckStatus,
    setRawCheckStatus,
    tags,
    setTags,
    wasteCategoryName,
    setWasteCategoryName,
    clientName,
    setClientName,
    clientCode,
    setClientCode,
    clientId,
    setClientId,
    clientAddress,
    setClientAddress,
    files,
    setFiles,
    toCharacterization,
    fetchContainerClientRegisteredInfo: fetchClientInfo,
  } = useCollectionRowClient(
    timeLine,
    dropOff,
    superContainers,
    corporationClients,
    historyCycle,
    imageData,
    azureUrl,
  );

  useEffect(() => {
    if (widthWindow < 1080) {
      setImageFrameSize(UI_CONSTANTS.IMAGES.COLLECTION_THUMBNAIL_MOBILE_SIZE);
      setImageInnerSize(
        UI_CONSTANTS.IMAGES.COLLECTION_THUMBNAIL_MOBILE_INNER_SIZE,
      );
    } else {
      setImageFrameSize(UI_CONSTANTS.IMAGES.COLLECTION_THUMBNAIL_SIZE);
      setImageInnerSize(UI_CONSTANTS.IMAGES.COLLECTION_THUMBNAIL_INNER_SIZE);
    }
  }, [widthWindow]);

  const handleSelectionChange = useCallback(
    (event) => {
      const value = event?.target?.value;
      if (!value) {
        setClientName("");
        setClientAddress("");
        setClientId("");
        return;
      }
      const selectedValue = JSON.parse(value);
      setClientName(selectedValue?.name);
      setClientAddress(selectedValue?.address);
      setClientId(selectedValue?._id);
    },
    [setClientName, setClientAddress, setClientId],
  );

  const fetchContainerClientRegisteredInfo = useCallback(
    async (id) => {
      // Recharger les données client après la sauvegarde
      if (fetchClientInfo) {
        await fetchClientInfo(id, null);
      }
    },
    [fetchClientInfo],
  );

  const saveOutputSurface = useCallback(
    async (waste_category, characterization) => {
      try {
        const historyEntries = resolveHistoryEntriesForFluxChange({
          superContainerTimeLine,
          timeLine,
          collectionData,
          historyCycleFallback: historyCycle,
        });

        if (!historyEntries.length) {
          console.warn("⚠️ Aucune collection trouvée, arrêt du traitement.");
          return;
        }

        await applyWasteCategoryToCycleSurfaces({
          wasteCategoryJson: waste_category,
          characterization,
          containerId: timeLine._id,
          historyEntries,
          azureUrl,
        });
      } finally {
        setDisplayClient(false);
        setNewClient(false);
        setDisplayStatus(false);
        setDisplayTags(false);
      }
    },
    [superContainerTimeLine, collectionData, timeLine, azureUrl, historyCycle],
  );

  const buildClientData = useCallback(
    () => ({
      name: clientName || "",
      code: clientCode || "",
      address: clientAddress || "",
      checkStatus: checkStatus
        ? typeof checkStatus === "string" && checkStatus.trim().startsWith("{")
          ? JSON.parse(checkStatus)
          : checkStatus
        : "-",
      tags: tags || [],
      ...(files?.length > 0 && { files }),
    }),
    [clientName, clientCode, clientAddress, checkStatus, tags, files],
  );

  const buildContainerData = useCallback(
    (id, blobName, commentsList, characterizedContainer) => ({
      id,
      weight:
        parseFloat(String(clientRegisteredWeight).replace(",", ".")) * 1000,
      time: timeLine._time,
      modified_time: moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
      client: JSON.stringify(buildClientData()),
      waste_category: wasteCategoryName,
      blobName,
      comments: JSON.stringify(commentsList),
      toCharacterization: characterizedContainer,
      addressName: timeLine.addressName,
      dropOff: historyCycle[historyCycle.length - 1]?._time,
      pickup: timeLine._time,
      mongoClientId: clientId,
    }),
    [
      clientRegisteredWeight,
      timeLine,
      buildClientData,
      wasteCategoryName,
      historyCycle,
      clientId,
    ],
  );

  const handleSubmit = useCallback(
    async (waste_category, changeWasteCategory, characterizedContainer) => {
      setLoadScreen(true);

      let commentsList = parseCommentsList(comments);
      if (comment?.toDelete || comment?.text !== "") {
        const filtered = commentsList.filter(
          (item) => item.time !== comment.time,
        );
        commentsList = comment?.toDelete ? filtered : [...filtered, comment];

        setComment({
          time: new Date().toISOString(),
          author: buildCommentAuthorFromStorage(),
          text: "",
        });
      }
      setComments(commentsList);
      if (wasteCategoryName && wasteCategoryName !== "{}") {
        try {
          setWasteName(JSON.parse(wasteCategoryName).wasteCategoryName);
        } catch (e) {
          // Ignore parsing errors
        }
      }

      // Toujours sauvegarder les données client (y compris les tags)
      // même si on ne change pas la catégorie de déchets
      let id = timeLine._id;
      if (superContainers?.containerObjectIdList?.includes(id)) {
        id = superContainers._id;
      }

      const cameraId = getCameraId(imageData.image_url);
      const blobName = `camera-ak_${cameraId}/output-client/${timeLine._time.replace(/:/g, "_").slice(0, -1)}-camera-ak_${cameraId}-${id}-client.json`;
      const blobUrl = azureUrl + blobName;

      try {
        const azureResponse = await getAzureBlob({ blobUrl });
        const mergedData = {
          ...azureResponse.data,
          ...buildContainerData(
            id,
            blobName,
            commentsList,
            characterizedContainer,
          ),
        };
        await saveContainerWeight(mergedData);
      } catch (error) {
        if (error.response?.status === 404) {
          await saveContainerWeight(
            buildContainerData(
              id,
              blobName,
              commentsList,
              characterizedContainer,
            ),
          );
        } else {
          console.error(error);
        }
      }

      if (changeWasteCategory) {
        await updateContentCategories(
          { contentCategory: JSON.parse(wasteCategoryName).id },
          timeLine._id,
        );
      }

      if (changeWasteCategory || characterizedContainer) {
        try {
          await saveOutputSurface(wasteCategoryName, characterizedContainer);

          const imageUrl = imageData?.image_url || historyCycle?.[0]?.image_url;
          const { cameraId: metricsCameraId, imageName } =
            parseCameraImageUrl(imageUrl);
          const dateRange = buildMetricsDateRangeFromHistoryCycle(
            historyCycle,
            timeLine._time,
          );

          if (metricsCameraId && dateRange) {
            await enqueueWasteStatisticsMetrics({
              cameraName: metricsCameraId,
              start: dateRange.start,
              stop: dateRange.stop,
              imageName,
            });
          } else {
            console.warn(
              "⚠️ Metrics non enqueued après changement de flux (camera/date manquants).",
            );
          }
        } catch (err) {
          console.error("saveOutputSurface failed:", err);
        }
      }

      setDisplayClient(false);
      setDisplayTags(false);
      setNewClient(false);
      setTotalWeight(clientRegisteredWeight);
      // Recharger les données client après la sauvegarde pour afficher les tags mis à jour
      await fetchContainerClientRegisteredInfo(id);

      setLoadScreen(false);
    },
    [
      comments,
      comment,
      wasteCategoryName,
      timeLine,
      superContainers,
      imageData,
      azureUrl,
      buildContainerData,
      saveOutputSurface,
      historyCycle,
      clientRegisteredWeight,
      fetchContainerClientRegisteredInfo,
      setLoadScreen,
      setComments,
      setComment,
      setWasteName,
      setDisplayClient,
      setNewClient,
      setTotalWeight,
    ],
  );

  const canCharacterize = canCharacterizeFromHistorique(imageData?.image_url);
  const characterizeBlockedReason = getCharacterizeFromHistoriqueBlockReasonKey(
    imageData?.image_url,
  );

  const handleSubmitSendToReview = useCallback(async () => {
    const blockReason = getCharacterizeFromHistoriqueBlockReasonKey(
      imageData?.image_url,
    );
    if (blockReason) {
      toast.error(t(blockReason), {
        className: "custom-toast",
      });
      return;
    }
    setLoadScreen(true);
    try {
      const cameraId = getCameraId(imageData.image_url);

      let collections = [];
      if (superContainerTimeLine) {
        const leftSide = superContainerTimeLine.find((i) =>
          i._value_image.includes("-L.jpeg"),
        );
        const collection =
          collectionData?.[`${leftSide?._measurement}_${leftSide?._time}`];
        collections = [...collection];
      } else {
        const collection =
          collectionData[`${timeLine._measurement}_${timeLine._time}`];
        collections = [collection];
      }
      collections.forEach((c) => {
        c.historyCurrent.forEach(async (h) => {
          await createCharacReview({
            container_id: h._measurement,
            camera_id: cameraId,
            time: h._time,
            imagesrc: h.image_url,
          });
        });
      });

      toast.success(t("Review request sent"), {
        className: "custom-toast",
      });
    } catch (e) {
      console.error("createCharacReview failed:", e);
      toast.error(t("Unable to send review request"), {
        className: "custom-toast",
      });
    } finally {
      fetchContainerClientRegisteredInfo(timeLine._id);
      setLoadScreen(false);
    }
  }, [
    imageData,
    superContainerTimeLine,
    collectionData,
    timeLine,
    t,
    fetchContainerClientRegisteredInfo,
    setLoadScreen,
  ]);

  const openPdfImageSelection = useCallback(() => {
    setReportingOptions(false);
    setOpenPdfImageSelectionModal(true);
  }, []);

  const prepareChartForExport = useCallback(() => {
    setChartData(
      buildCollectionRowChartData({
        timeLine,
        superContainerTimeLine,
        collectionData,
        t,
      }),
    );
    setDisplayChartData(true);
  }, [
    collectionData,
    setChartData,
    setDisplayChartData,
    superContainerTimeLine,
    t,
    timeLine,
  ]);

  const handleOpenTags = useCallback(() => {
    const isDisabled = timeLine?.fictiveContainer?.active;
    if (!historyCycle || historyCycle.length === 0 || isDisabled) {
      return;
    }
    setDisplayClient(true);
    setNewClient(false);
    setDisplayStatus(false);
    setDisplayTags(true);
  }, [
    historyCycle,
    timeLine,
    setDisplayClient,
    setNewClient,
    setDisplayStatus,
    setDisplayTags,
  ]);

  const handleOpenCollectionInfo = useCallback(() => {
    const isDisabled = timeLine?.fictiveContainer?.active;
    if (!historyCycle || historyCycle.length === 0 || isDisabled) {
      return;
    }
    setDisplayClient(true);
    setNewClient(false);
    setDisplayStatus(false);
    setDisplayTags(false);
  }, [
    historyCycle,
    timeLine,
    setDisplayClient,
    setNewClient,
    setDisplayStatus,
    setDisplayTags,
  ]);

  const handleOpenContainerSettings = useCallback(() => {
    const id = timeLine?._id ?? timeLine?._measurement ?? timeLine?.id;
    if (!id) return;
    history.push(`/settings/containers/${id}`, {
      container: timeLine,
      from: "activity",
      returnPath: location.pathname + location.search,
    });
  }, [history, location.pathname, location.search, timeLine]);

  const handleOpenDetail = useCallback(() => {
    if (!timeLine?._measurement || !timeLine?._time) return;

    setDisplayChartData(false);
    const resolvedWasteCategory = resolveActiveWasteCategoryFromName(
      wasteCategoryName,
      timeLine?.wasteName || "",
    );

    setActiveWasteCategory(resolvedWasteCategory);

    const detailState = {
      timeLine,
      superContainerTimeLine,
      activeWasteCategory: resolvedWasteCategory,
      superContainers,
    };

    try {
      sessionStorage.setItem(
        ACTIVITY_DETAIL_STORAGE_KEY,
        JSON.stringify(detailState),
      );
    } catch {
      // ignore storage errors
    }

    history.push("/activity/detail", detailState);
  }, [
    timeLine,
    setDisplayChartData,
    setActiveWasteCategory,
    wasteCategoryName,
    superContainerTimeLine,
    superContainers,
    history,
  ]);

  const pdfImageSelectionData = useMemo(() => {
    if (!openPdfImageSelectionModal)
      return { allImages: [], defaultSelectedUrls: new Set() };
    const collections = extractCollections(
      superContainerTimeLine,
      collectionData,
      timeLine,
      historyCycle,
    );
    if (!collections?.length)
      return { allImages: [], defaultSelectedUrls: new Set() };
    const allImages = collections
      .flatMap((col, i) =>
        (col.historyCycle || []).map((item) => ({ item, collectionIndex: i })),
      )
      .sort((a, b) => new Date(a.item._time) - new Date(b.item._time));
    const defaultSelectedUrls = new Set(
      collections.flatMap((col) =>
        getUniqueHistoryCycle(col.historyCycle || []).map(
          (item) => item.image_url,
        ),
      ),
    );
    return { allImages, defaultSelectedUrls };
  }, [
    openPdfImageSelectionModal,
    superContainerTimeLine,
    collectionData,
    timeLine,
    historyCycle,
  ]);

  const generatePdf = useCallback(
    async (selectedImageUrls) => {
      setReportingOptions(false);
      setIsProgress(false);
      setLoadScreen(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 2000));

        const controller = new AbortController();
        const { pdfContent, pdfFilename } =
          await GenerateAndDownloadFillingCyclePdf({
            refs: { chartRef, cycleChartRef, materialChartRef },
            user,
            superContainerTimeLine,
            collectionData,
            timeLine,
            historyCycle,
            controller,
            t,
            formattedColorMaterial,
            getTemporalData,
            imageDimensions: async (src) => {
              try {
                const { default: reactImageSize } = await import(
                  "react-image-size"
                );
                if (!src) throw new Error("Image source is undefined or empty");
                const dimensions = await reactImageSize(src);
                return dimensions;
              } catch (error) {
                console.error(
                  `Error getting image dimensions for ${src}:`,
                  error.message,
                );
                return { width: 0, height: 0 };
              }
            },
            reduceImageQuality,
            azureSASTokenToUrl,
            starsCalculation,
            clientRegisteredWeight,
            numberOfPackmat,
            comments,
            wasteName,
            clientName,
            dropOff,
            collectionFillingLevel,
            i18n,
            selectedImageUrls,
          });

        const mainPdfBlob = await pdf(pdfContent).toBlob();
        const pdfList = [{ filename: pdfFilename, pdfBlob: mainPdfBlob }];

        if (exportFiles?.length) {
          const azureFiles = await Promise.all(
            exportFiles.map(async (filePath) => {
              const { data: fileBlob } = await getAzureBlobFile({
                blobUrl: `${azureUrl}${filePath}`,
              });
              const fileName = filePath.split("/").pop();
              return { filename: fileName, pdfBlob: fileBlob };
            }),
          );

          pdfList.push(...azureFiles);
          const mergedPdfBlob = await mergePdfs(pdfList);
          setPdfFile([{ filename: pdfFilename, pdfBlob: mergedPdfBlob }]);
        } else {
          setPdfFile(pdfList);
        }
        setOpenModalExport(true);
        setOpenPdfImageSelectionModal(false);
        setExportFiles([]);
      } catch (error) {
        console.error("Failed to generate compliance PDF:", error);
        setOpenPdfImageSelectionModal(false);
      } finally {
        setDisplayChartData(false);
        setLoadScreen(false);
      }
    },
    [
      chartRef,
      cycleChartRef,
      materialChartRef,
      user,
      superContainerTimeLine,
      collectionData,
      timeLine,
      historyCycle,
      t,
      clientRegisteredWeight,
      numberOfPackmat,
      comments,
      wasteName,
      clientName,
      dropOff,
      collectionFillingLevel,
      i18n,
      exportFiles,
      azureUrl,
      setIsProgress,
      setLoadScreen,
      setDisplayChartData,
    ],
  );

  return (
    <section className="time-line-element" key={index}>
      <div className="w-full my-3 bg-white rounded-lg border-[1.2px] border-[#D3D3D3] flex flex-col overflow-hidden">
        <div
          className={`flex w-full p-4 gap-4 ${widthWindow < 1080 ? "flex-col" : "flex-row"}`}
        >
          <div
            className="flex-shrink-0"
            style={getActivityImageFrameStyle(imageFrameSize)}
          >
            {imageData && (
              <CropImage
                container={timeLine}
                data={imageData}
                imgDivWidth={imageInnerSize}
                imgDivHeight={imageInnerSize}
                isTooltip={false}
                isMap={false}
                isCharacterization={false}
                displayLegend={false}
                materialsDropdownToDisplay={[]}
                showCheckbox={false}
                skipSurfaceDataFetch={true}
                onAfterSegmentationUpdate={refetchRowData}
              />
            )}
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <CollectionRowHeader
              wasteName={wasteName}
              timeLine={timeLine}
              checkStatus={checkStatus}
              setCheckStatus={setCheckStatus}
              rawCheckStatus={rawCheckStatus}
              setRawCheckStatus={setRawCheckStatus}
              savingStatus={savingStatus}
              setSavingStatus={setSavingStatus}
              loading={loading}
              superContainers={superContainers}
              imageData={imageData}
              azureUrl={azureUrl}
              t={t}
              onOpenCollectionInfo={handleOpenCollectionInfo}
              onOpenTags={handleOpenTags}
              onOpenFiles={() => setOpenFileModal(true)}
              onOpenNotes={() => setDisplayComment(true)}
              onOpenDetail={handleOpenDetail}
              onOpenCharacterize={() =>
                setOpenModalToCharacterization((prev) => !prev)
              }
              toCharacterization={toCharacterization}
              canCharacterize={canCharacterize}
              characterizeBlockedReason={characterizeBlockedReason}
              onOpenContainerSettings={handleOpenContainerSettings}
              onRequestPdf={() => {
                prepareChartForExport();
                setInitialPdfModalAction("export");
                if (files.length > 0) {
                  setReportingOptions(true);
                } else {
                  openPdfImageSelection();
                }
              }}
              onRequestEmail={() => {
                prepareChartForExport();
                setInitialPdfModalAction("share");
                if (files.length > 0) {
                  setReportingOptions(true);
                } else {
                  openPdfImageSelection();
                }
              }}
              onRequestCsv={() => {
                setActiveWasteCategory(
                  resolveActiveWasteCategoryFromName(wasteCategoryName),
                );
                handleCsvExport(
                  historyCycle,
                  timeLine,
                  clientName,
                  clientCode,
                  clientRegisteredWeight,
                );
              }}
            />

            <CollectionRowTags
              timeLine={timeLine}
              tags={tags}
              toCharacterization={toCharacterization}
              clientName={clientName}
              t={t}
              onMidCycleFluxValidated={(updatedContainer) => {
                if (updatedContainer?.midCycleFluxChange) {
                  timeLine.midCycleFluxChange =
                    updatedContainer.midCycleFluxChange;
                } else if (timeLine.midCycleFluxChange) {
                  timeLine.midCycleFluxChange.active = false;
                }
              }}
            />

            <CollectionRowInfo
              timeLine={timeLine}
              historyCycle={historyCycle}
              collectionFillingLevel={collectionFillingLevel}
              numberOfPackmat={numberOfPackmat}
              clientRegisteredWeight={clientRegisteredWeight}
              toCharacterization={toCharacterization}
              weightToTonnes={weightToTonnes}
              toFrenchNumber={toFrenchNumber}
              loading={loading}
              files={files}
              notesCount={comments.length}
              onOpenFiles={() => setOpenFileModal(true)}
              onOpenNotes={() => setDisplayComment(true)}
              loadScreen={loadScreen}
              t={t}
            />
          </div>
        </div>

        {timeLine.activatedSortingErrorOption && (
          <SortingQualityFooter
            isLoading={loading || !historyCycle?.length}
            numberOfAnomaly={numberOfAnomaly}
            numberOfStars={numberOfStars}
            totalWasteVolume={totalWasteVolume}
            totalVolumeSortingError={totalVolumeSortingError}
            totalMassSortingError={totalMassSortingError}
          />
        )}

        {index === itemIndex && (
          <ActivityTimeLineTooltip
            data={timeLine}
            setItemIndex={setItemIndex}
            activeWasteCategory={activeWasteCategory}
          />
        )}

        <PdfImageSelectionModal
          isOpen={openPdfImageSelectionModal}
          onClose={() => {
            setOpenPdfImageSelectionModal(false);
            setDisplayChartData(false);
          }}
          allImages={pdfImageSelectionData.allImages}
          defaultSelectedUrls={pdfImageSelectionData.defaultSelectedUrls}
          azureSASTokenToUrl={azureSASTokenToUrl}
          onConfirm={generatePdf}
          t={t}
        />

        <CollectionRowModals
          timeLine={timeLine}
          openModalExport={openModalExport}
          setOpenModalExport={setOpenModalExport}
          setLoadScreen={setLoadScreen}
          pdfFile={pdfFile}
          setPdfFile={setPdfFile}
          initialPdfModalAction={initialPdfModalAction}
          displayClient={displayClient}
          setDisplayClient={setDisplayClient}
          weightRef={weightRef}
          newClient={newClient}
          setNewClient={setNewClient}
          handleSelectionChange={handleSelectionChange}
          setClientCode={setClientCode}
          clientId={clientId}
          setClientName={setClientName}
          setClientAddress={setClientAddress}
          clientRegisteredWeight={clientRegisteredWeight}
          setClientRegisteredWeight={setClientRegisteredWeight}
          wasteCategoryName={wasteCategoryName}
          setWasteCategoryName={setWasteCategoryName}
          wasteCategory={wasteCategory}
          saveWeight={handleSubmit}
          totalWeight={totalWeight}
          superContainers={superContainers}
          loading={loading}
          tags={tags}
          setTags={setTags}
          displayStatus={displayStatus}
          setDisplayStatus={setDisplayStatus}
          displayTags={displayTags}
          setDisplayTags={setDisplayTags}
          wasteName={wasteName}
          displayComment={displayComment}
          setDisplayComment={setDisplayComment}
          comments={comments}
          comment={comment}
          setComment={setComment}
          reviewComments={reviewComments}
          setReviewComments={() => {}}
          imageData={imageData}
          openFileModal={openFileModal}
          setOpenFileModal={setOpenFileModal}
          outputClient={outputClient}
          setOutputClient={setOutputClient}
          files={files}
          setFiles={setFiles}
          clientName={clientName}
          clientCode={clientCode}
          clientAddress={clientAddress}
          checkStatus={checkStatus}
          openModalToCharacterization={openModalToCharacterization}
          setOpenModalToCharacterization={setOpenModalToCharacterization}
          handleSubmitSendToReview={handleSubmitSendToReview}
          reportingOptions={reportingOptions}
          setReportingOptions={setReportingOptions}
          exportFiles={exportFiles}
          setExportFiles={setExportFiles}
          generatePdf={generatePdf}
          onRequestPdfImageSelection={openPdfImageSelection}
          t={t}
        />
      </div>
    </section>
  );
};

export default ActivityCollectionRow;
